import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Skeleton,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { RefObject, useMemo, useRef, useState } from 'react';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { Bank, Util } from '@finley-technologies/finley-types';

import { formatCurrencyAmount } from 'packages/common-frontend/utils';
import {
  useGenerateSummaryPeriodStatementQuery,
  useGetStatementQuery,
} from 'packages/reporting/requests/reporting.gql.generated';
import { useGetEarliestAndLatestTransactionDatesQuery } from 'packages/transactions/requests/transactions.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { CommonFacilitySelect } from '~/packages/credit-facility/ui/CreditFacilitySelect';
import {
  MonthlyStatementDailyAggregateTable,
  MonthlyStatementSummaryBalance,
} from '~/packages/monthlyStatement';
import { TransactionsFilterState } from '~/packages/transactions/hooks/transactionsHooks';
import { TransactionsDailyLineItemsTable } from '~/packages/transactions/ui/TransactionsDailyLineItemsTable';

// constantS
const TABLE_HEADERS = ['Dates', 'Balance'];

// styles
const headerStyles = {
  backgroundColor: colors.background[200],
  color: colors.black[95],
  fontSize: '1rem',
  fontWeight: 500,
  lineHeight: '1.5rem',
  position: 'sticky',
  verticalAlign: 'top',
};

type SingleStatementViewProps = {
  dailyResolvedAggregateLineItemProps: TransactionsFilterState;
  selectedCreditFacilityId: string;
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
};

const SingleStatementView = ({
  dailyResolvedAggregateLineItemProps,
  selectedCreditFacilityId,
  startDate,
  endDate,
}: SingleStatementViewProps) => {
  const {
    error,
    data: statementDataQueryResult,
    loading,
  } = useGetStatementQuery({
    skip: startDate === undefined || endDate === undefined,
    variables: {
      creditFacilityId: selectedCreditFacilityId,
      startDate,
      endDate,
    },
  });

  const statementData = statementDataQueryResult?.generateStatement;

  if (loading || !statementData) return <Skeleton height="600px" />;
  if (error) throw error;

  return (
    <>
      {/* // TODO: 1. eventually add info icons @haghanim @jendevelops */}
      {/* // TODO: 2. try to merge with MSR @haghanim */}
      <MonthlyStatementSummaryBalance
        totalBalance={statementData.summary.totalBalance}
        balances={statementData.summary.balances}
      />
      <Tabs width="100%">
        <TabList>
          <Tab>Totals</Tab>
          <Tab>Line Items</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px="0">
            <MonthlyStatementDailyAggregateTable
              // TODO: @sachinr4 and @haghanim reverse rows on BE - https://linear.app/finleycms/issue/ENG-11260/add-reverse-rows-variable-to-usegetstatementquery-api-to-render
              dailyAggregateData={statementData}
            />
          </TabPanel>
          <TabPanel sx={{ px: '0', pt: '0' }}>
            <TransactionsDailyLineItemsTable
              {...dailyResolvedAggregateLineItemProps}
              startDate={startDate}
              endDate={endDate}
              creditFacilityIds={[selectedCreditFacilityId]}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

const TransactionsDrawerListings = ({
  setActiveStatementTab,
  setSelectedDateRange,
  earliestDate,
  latestDate,
  selectedCreditFacilityId,
}: {
  setActiveStatementTab: (tab: 'summary' | 'single') => void;
  setSelectedDateRange: (dateRange: {
    startDate: Util.FullDateStr;
    endDate: Util.FullDateStr;
  }) => void;
  earliestDate: Util.FullDateStr;
  latestDate: Util.FullDateStr;
  selectedCreditFacilityId: string;
}) => {
  const {
    error: summaryPeriodStatementError,
    data: summaryPeriodStatement,
    loading,
  } = useGenerateSummaryPeriodStatementQuery({
    variables: {
      creditFacilityId: selectedCreditFacilityId,
      startDate: earliestDate,
      endDate: latestDate,
      cadence: 'month', // TODO: @markhaghani, add cadence optionality
    },
    fetchPolicy: 'no-cache',
  });

  if (loading) return <Skeleton height="600px" width="100%" />;
  if (summaryPeriodStatementError) throw summaryPeriodStatementError;
  if (!summaryPeriodStatement)
    return <Text>No statements found for this facility.</Text>;

  return (
    <TableContainer sx={{ mb: '2rem', width: '100%' }}>
      <Table
        borderColor={colors.gray[100]}
        sx={{
          borderWidth: 'thin',
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative',
          fontSize: '1rem',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th key={header} sx={headerStyles}>
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody sx={{ textColor: colors.black[70] }}>
          {summaryPeriodStatement?.summary.map((summaryPeriodData) => (
            <Tr
              onClick={() => {
                setActiveStatementTab('single');
                setSelectedDateRange({
                  startDate: summaryPeriodData.startDate,
                  endDate: summaryPeriodData.endDate,
                });
              }}
              sx={{
                _hover: { backgroundColor: colors.gray[100] },
              }}
              key={summaryPeriodData.startDate}
            >
              <Td>{`${commonDate.fullDateStrToHumanDateString(
                summaryPeriodData.startDate,
              )} - ${commonDate.fullDateStrToHumanDateString(
                commonDate.endOfPeriod(summaryPeriodData.endDate, 'month'),
              )}`}</Td>
              <Td>
                {formatCurrencyAmount(
                  summaryPeriodData.totalEndingBalance,
                  Bank.Currency.USD,
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const TransactionsStatementsDrawerContent = ({
  drawerControls,
  statementsBtnRef,
  dailyResolvedAggregateLineItemProps,
  lockedCreditFacilityId,
}: {
  drawerControls: ReturnType<typeof useDisclosure>;
  statementsBtnRef: RefObject<HTMLButtonElement>;
  dailyResolvedAggregateLineItemProps: TransactionsFilterState;
  lockedCreditFacilityId?: string;
}) => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const initialCreditFacilityIdsToName = useMemo(() => {
    const entries = facilities.map(({ id, name }) => [id, name] as const);
    return Object.fromEntries(entries);
  }, [facilities]);
  finleyAssert(
    Object.keys(initialCreditFacilityIdsToName).length > 0,
    'initialCreditFacilityIdsToName had 0 facilities',
  );

  // Determine initial selected facility and if the selector should be disabled
  const isLockedToSingleFacility = Boolean(lockedCreditFacilityId);
  const initialFacilityId =
    lockedCreditFacilityId || Object.keys(initialCreditFacilityIdsToName)[0];

  const [
    selectedCreditFacilityId,
    setSelectedCreditFacilityId,
  ] = useState<string>(initialFacilityId);

  const [activeStatementTab, setActiveStatementTab] = useState<
    'summary' | 'single'
  >('summary');

  const [selectedDateRange, setSelectedDateRange] = useState<{
    startDate: Util.FullDateStr | undefined;
    endDate: Util.FullDateStr | undefined;
  }>({
    startDate: undefined,
    endDate: undefined,
  });

  const {
    error: earliestAndLatestTransactionDatesError,
    data: earliestAndLatestTransactionDates,
    loading: datesLoading,
  } = useGetEarliestAndLatestTransactionDatesQuery({
    variables: {
      creditFacilityId: selectedCreditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  const earliestDate = earliestAndLatestTransactionDates?.dates?.earliestDate;
  const latestDate = earliestAndLatestTransactionDates?.dates?.latestDate;

  if (earliestAndLatestTransactionDatesError)
    throw earliestAndLatestTransactionDatesError;

  return (
    <Drawer
      {...drawerControls}
      placement="right"
      finalFocusRef={statementsBtnRef}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader textAlign="left" my="2rem" px="3rem" pb="0">
          {activeStatementTab === 'single' && (
            <Button
              variant="v2-ghost"
              colorScheme="gray"
              sx={{
                size: 'sm',
                fontWeight: 'normal',
                mb: '1rem',
              }}
              leftIcon={<Icon icon="chevron-left" />}
              onClick={() => {
                setActiveStatementTab('summary');
              }}
            >
              Back
            </Button>
          )}
          <Heading>
            {activeStatementTab === 'summary' ||
            !selectedDateRange.startDate ||
            !selectedDateRange.endDate
              ? 'Statements'
              : `${commonDate.fullDateStrToHumanDateString(
                  selectedDateRange.startDate,
                )} - ${commonDate.fullDateStrToHumanDateString(
                  commonDate.endOfPeriod(selectedDateRange.endDate, 'month'),
                )}`}
          </Heading>
        </DrawerHeader>

        <DrawerBody px="3rem">
          {activeStatementTab === 'summary' && (
            <VStack align="flex-start" gap="2rem">
              <CommonFacilitySelect
                isSearchable
                flexProps={{ width: '100%' }}
                menuButtonProps={{ width: '100%' }}
                menuListProps={{ width: '100%' }}
                matchWidth
                selectedFacilityId={selectedCreditFacilityId}
                onFacilitySelect={(cfId: string) => {
                  setSelectedCreditFacilityId(cfId);
                }}
                isDisabled={isLockedToSingleFacility}
              />
              {(() => {
                if (datesLoading)
                  return <Skeleton height="200px" width="100%" />;

                if (earliestDate && latestDate)
                  return (
                    <TransactionsDrawerListings
                      setActiveStatementTab={setActiveStatementTab}
                      setSelectedDateRange={setSelectedDateRange}
                      earliestDate={earliestDate}
                      latestDate={latestDate}
                      selectedCreditFacilityId={selectedCreditFacilityId}
                    />
                  );

                return <Text>No statements found for this facility.</Text>;
              })()}
            </VStack>
          )}

          {activeStatementTab === 'single' &&
            selectedDateRange.startDate &&
            selectedDateRange.endDate && (
              <SingleStatementView
                dailyResolvedAggregateLineItemProps={
                  dailyResolvedAggregateLineItemProps
                }
                selectedCreditFacilityId={selectedCreditFacilityId}
                startDate={selectedDateRange.startDate}
                endDate={selectedDateRange.endDate}
              />
            )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const TransactionsStatementsDrawer = ({
  creditFacilityIds,
  loanIds,
  accountTypes,
  startDate,
  endDate,
  lockedCreditFacilityId,
}: TransactionsFilterState & {
  lockedCreditFacilityId?: string;
}) => {
  const statementsBtnRef = useRef(null);

  const drawerControls = useDisclosure();

  return (
    <>
      <Button
        variant="secondary"
        colorScheme="v2-aurora"
        ref={statementsBtnRef}
        onClick={drawerControls.onOpen}
      >
        Statements
      </Button>
      <TransactionsStatementsDrawerContent
        drawerControls={drawerControls}
        statementsBtnRef={statementsBtnRef}
        dailyResolvedAggregateLineItemProps={{
          creditFacilityIds,
          loanIds,
          accountTypes,
          startDate,
          endDate,
        }}
        lockedCreditFacilityId={lockedCreditFacilityId}
      />
    </>
  );
};
